import { useMemo, useState } from 'react';

import { FIELD_FORMATS, FieldFormatSpec } from 'constants/index';

import './list-zero-workflow.scss';

/*
 * Field formats reference. Lists the expected format for every employee field
 * the system checks when a ticket is fixed and promoted. Driven by the shared
 * FIELD_FORMATS constant, which the Fix & Promote dialog uses too, so the two
 * stay in sync.
 */

function AllowedValues({ values }: { values?: string[] }) {
  if (!values || values.length === 0) return null;
  return (
    <div className="fields-allowed">
      {values.map((v) => (
        <span key={v} className="fields-allowed__chip">
          {v}
        </span>
      ))}
    </div>
  );
}

function FieldRow({ spec }: { spec: FieldFormatSpec }) {
  return (
    <tr>
      <td>
        <div className="fields-name">{spec.field}</div>
        <div className="fields-label">{spec.label}</div>
        {!spec.validated && (
          <span className="fields-tag">Guidance only</span>
        )}
      </td>
      <td className="fields-applies">{spec.appliesTo}</td>
      <td>
        <div>{spec.format}</div>
        <AllowedValues values={spec.allowed} />
        {spec.note && <div className="fields-note">{spec.note}</div>}
      </td>
      <td>
        {spec.example ? (
          <code className="fields-example">{spec.example}</code>
        ) : (
          <span className="fields-dash">-</span>
        )}
      </td>
    </tr>
  );
}

export default function ListZeroFields() {
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FIELD_FORMATS;
    return FIELD_FORMATS.filter(
      (s) =>
        s.field.toLowerCase().includes(q) ||
        s.label.toLowerCase().includes(q) ||
        s.appliesTo.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className="list-zero-workflow">

      <section className="workflow-card">
        <h2>Field Formats</h2>
        <p>
          When a ticket has the status "With Error &amp; Waiting for updates", the
          system has found a field whose value is missing or in the wrong format.
          This page lists the format the system expects for each field, so you can
          correct the value and promote the ticket.
        </p>

        <div className="workflow-callout workflow-callout--danger">
          For fields that take a value from a list, the value must match one the
          system recognises. Check the spelling and punctuation carefully, as an
          unexpected value is rejected.
        </div>

        <input
          className="fields-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by field name, e.g. VISAISSUANCE"
        />
      </section>

      <section className="workflow-card">
        {rows.length === 0 ? (
          <p className="status-empty">No field matches "{query}".</p>
        ) : (
          <table className="workflow-table fields-table">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Field</th>
                <th style={{ width: 220 }}>Applies to</th>
                <th>Expected format</th>
                <th style={{ width: 190 }}>Example</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((spec) => (
                <FieldRow key={spec.field} spec={spec} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
